import { useCallback, useRef, KeyboardEvent, RefObject } from 'react'

export type TabOrientation = 'horizontal' | 'vertical'

export type UseTabListOptions<T> = {
	tabs: T[]
	activeIndex: number
	onActivate: (index: number) => void
	orientation?: TabOrientation
	idPrefix: string
}

export type UseTabListReturn = {
	getTabProps: (index: number) => {
		role: 'tab'
		id: string
		'aria-selected': boolean
		tabIndex: number
		onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void
	}
	getTabPanelProps: (index: number) => {
		role: 'tabpanel'
		id: string
		'aria-labelledby': string
		hidden: boolean
	}
	focusTab: (index: number) => void
	tabRefs: RefObject<(HTMLButtonElement | null)[]>
}

/**
 * Shared ARIA tab pattern implementation.
 *
 * Implements roving tabindex and arrow-key navigation for `role="tablist"`.
 * - Only the active tab is in the natural tab order (`tabindex="0"`).
 * - Arrow keys move focus/activation between tabs.
 * - Home/End jump to first/last tab.
 * - Tab moves into/out of the tablist without traversing every tab.
 *
 * Use for in-page content switching (e.g. scope tabs, insights panels).
 * Do not use for route-changing navigation — use real links for that.
 */
export function useTabList<T>({
	tabs,
	activeIndex,
	onActivate,
	orientation = 'horizontal',
	idPrefix,
}: UseTabListOptions<T>): UseTabListReturn {
	const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
	const tabId = (index: number) => `${idPrefix}-tab-${index}`
	const tabPanelId = (index: number) => `${idPrefix}-tabpanel-${index}`

	const focusTab = useCallback((index: number) => {
		tabRefs.current[index]?.focus()
	}, [])

	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLButtonElement>, index: number) => {
			const isVertical = orientation === 'vertical'
			let nextIndex = index

			switch (event.key) {
				case isVertical ? 'ArrowDown' : 'ArrowRight':
					nextIndex = (index + 1) % tabs.length
					break
				case isVertical ? 'ArrowUp' : 'ArrowLeft':
					nextIndex = (index - 1 + tabs.length) % tabs.length
					break
				case 'Home':
					nextIndex = 0
					break
				case 'End':
					nextIndex = tabs.length - 1
					break
				default:
					return
			}

			event.preventDefault()
			onActivate(nextIndex)
			focusTab(nextIndex)
		},
		[tabs.length, orientation, onActivate, focusTab]
	)

	const getTabProps = useCallback(
		(index: number) => {
			const isActive = index === activeIndex
			return {
				role: 'tab' as const,
				id: tabId(index),
				'aria-selected': isActive,
				tabIndex: isActive ? (0 as const) : (-1 as const),
				onKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => handleKeyDown(event, index),
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeIndex, handleKeyDown, idPrefix]
	)

	const getTabPanelProps = useCallback(
		(index: number) => {
			return {
				role: 'tabpanel' as const,
				id: tabPanelId(index),
				'aria-labelledby': tabId(index),
				hidden: index !== activeIndex,
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[activeIndex, idPrefix]
	)

	return { getTabProps, getTabPanelProps, focusTab, tabRefs }
}
